import type { FC } from 'react';

import { QuotedPostPreview } from '@shared/dream-components';

import type { CommunityPost } from '~/interfaces/community';
import { communityImageSrc } from '~/routes/community/_utils/communityImageSrc';
import { isDuplicateEmbedTitle } from '~/routes/community/_utils/postEmbedDedupe';
import { extractExcerpt, extractThumbnail } from '~/routes/community/_utils/tiptapPreview';

import { PostActionMenu } from '../../../PostActionMenu';
import { PostAuthorHeader } from '../../../PostCard/_components/PostAuthorHeader';
import { PostEmbedCard } from '../../../PostEmbedCard';
import { PostLink } from '../../../PostLink';

interface PinnedPostContentProps {
  post: CommunityPost;
  hideSpace: boolean;
}

export const PinnedPostContent: FC<PinnedPostContentProps> = ({ post, hideSpace }) => {
  const excerpt = extractExcerpt(post.tiptap_state);
  const hasEmbed = post.attachable?.type === 'post' || post.attachable?.type === 'quoted_post';
  const thumbnailSrc = hasEmbed ? null : extractThumbnail(post.tiptap_state);
  const showTitle = Boolean(post.title) && !isDuplicateEmbedTitle(post);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <PostAuthorHeader
          author={post.author}
          createdAt={post.created_at}
          editedAt={post.edited_at}
          space={post.space}
          hideSpace={hideSpace}
          size="compact"
        />
        <PostActionMenu post={post} triggerVariant="default" className="shrink-0" alignmentOffset={6} />
      </div>

      <div className="flex gap-4">
        <div className="min-w-0 flex-1">
          {showTitle && (
            <PostLink postId={post.id} className="font-bold text-[19px] leading-6 text-black line-clamp-2 no-underline">
              {post.title}
            </PostLink>
          )}
          {excerpt && <p className="mt-1 text-sm leading-5 text-black/70 line-clamp-3">{excerpt}</p>}
          {post.attachable?.type === 'post' && (
            <div className="mt-3">
              <PostEmbedCard postId={post.id} attachable={post.attachable} />
            </div>
          )}
          {post.attachable?.type === 'quoted_post' && (
            <div className="mt-3">
              <PostLink
                postId={post.attachable.id}
                className="block rounded-2xl transition-colors hover:bg-black/[0.02]"
              >
                <QuotedPostPreview post={post.attachable} />
              </PostLink>
            </div>
          )}
        </div>
        {thumbnailSrc && (
          <img
            data-testid="pinned-thumbnail"
            src={communityImageSrc(thumbnailSrc, 176)}
            alt=""
            loading="lazy"
            className="size-[88px] shrink-0 self-center rounded-xl object-cover bg-black/5"
          />
        )}
      </div>
    </div>
  );
};
